import { useEffect, useRef } from 'react';

export interface UseDisplayListLogicParams<T> {
    data?: T[];
    originalData?: T[];
    isLoading: boolean;
    isFetching?: boolean;
    isError?: boolean;
    searchText?: string;
    filters?: Record<string, any>;
}

export function useDisplayListLogic<T>({
    data = [],
    originalData = [],
    isLoading,
    isFetching = false,
    isError = false,
    searchText = '',
    filters = {},
}: UseDisplayListLogicParams<T>) {
    const hasLoadedOnceWithData = useRef(false);
    const hasStartedLoading = useRef(false);

    useEffect(() => {
        hasLoadedOnceWithData.current = false;
        hasStartedLoading.current = false;
    }, []);

    useEffect(() => {
        if (data.length > 0) {
            hasLoadedOnceWithData.current = true;
        }
    }, [data]);

    const noFilters =
        !filters ||
        (Array.isArray(filters) && filters.length === 0) ||
        (typeof filters === 'object' &&
            filters !== null &&
            Object.keys(filters).length === 0);

    const noSearchText = !searchText || searchText.trim() === '';

    const effectiveLoading =
        isLoading || (isFetching && noFilters && noSearchText);

    useEffect(() => {
        if (effectiveLoading) {
            hasStartedLoading.current = true;
        }
    }, [effectiveLoading]);

    const hasSearch = searchText.trim().length > 0;
    const hasFilters = Object.keys(filters).length > 0;
    const hasActiveSearchOrFilter = hasSearch || hasFilters;

    const showTable =
        !isLoading &&
        !isError &&
        (data.length > 0 ||
            hasActiveSearchOrFilter ||
            originalData.length > 0 ||
            hasLoadedOnceWithData.current);

    const showEmptyState =
        !isLoading &&
        !isError &&
        data.length === 0 &&
        !hasActiveSearchOrFilter &&
        !hasLoadedOnceWithData.current &&
        hasStartedLoading.current;

    return {
        showTable,
        showEmptyState,
    };
}
